// REQ-P1-06b: 共通チャットリアクションの契約型付きAPI。

import { api, type paths } from './client';

type ChatReactionRequest = NonNullable<
  paths['/artist/messages/reactions/{messageId}']['post']['requestBody']
>['content']['application/json'];
type ChatReactionsResponse = NonNullable<
  paths['/artist/messages/reactions/{messageId}']['get']['responses'][200]['content']
>['application/json'];

type ChatReactionScope = 'individual' | 'event' | 'group';
type ChatReactionMode = 'admin' | 'artist';

type ChatReactionParams = {
  mode?: ChatReactionMode;
  scope?: ChatReactionScope;
  artistId?: string;
  eventId?: string;
  threadId?: string;
  messageId?: string;
  reactionType?: string;
};

type RequestOptions = {
  signal?: AbortSignal;
};

function requireParam(value: string | undefined, name: string): string {
  if (!value) throw new Error(`${name} is required`);
  return value;
}

async function readOptionalJson(data: unknown, response: Response): Promise<Record<string, unknown>> {
  if (data && typeof data === 'object') return data as Record<string, unknown>;
  try {
    const json = await response.clone().json();
    return json && typeof json === 'object' ? (json as Record<string, unknown>) : {};
  } catch (_error) {
    return {};
  }
}

async function ensurePayloadOk(
  data: unknown,
  response: Response
): Promise<Record<string, unknown>> {
  const payload = await readOptionalJson(data, response);
  if (response.ok && payload?.success !== false) return payload;
  const error = new Error(String(payload?.message || payload?.error || `HTTP ${response.status}`));
  (error as Error & { status?: number; payload?: Record<string, unknown> }).status = response.status;
  (error as Error & { status?: number; payload?: Record<string, unknown> }).payload = payload;
  throw error;
}

function normalizeReactionBody(reactionType?: string): ChatReactionRequest {
  return { reactionType: reactionType || '' } as ChatReactionRequest;
}

export async function fetchChatMessageReactionDetails(
  params: ChatReactionParams,
  options: RequestOptions = {}
): Promise<ChatReactionsResponse> {
  const mode = params.mode || 'admin';
  const scope = params.scope || 'individual';
  const messageId = requireParam(params.messageId, 'messageId');

  if (scope === 'group') {
    const threadId = requireParam(params.threadId, 'threadId');
    const result =
      mode === 'artist'
        ? await api.GET('/artist/group-chat/threads/{threadId}/reactions/{messageId}', {
            params: { path: { threadId, messageId } },
            signal: options.signal,
          })
        : await api.GET('/admin/group-chat/threads/{threadId}/reactions/{messageId}', {
            params: { path: { threadId, messageId } },
            signal: options.signal,
          });
    return ensurePayloadOk(result.data, result.response) as Promise<ChatReactionsResponse>;
  }

  if (scope === 'event') {
    const eventId = requireParam(params.eventId, 'eventId');
    const { data, response } = await api.GET('/event-chat/events/{eventId}/reactions/{messageId}', {
      params: { path: { eventId, messageId } },
      signal: options.signal,
    });
    return ensurePayloadOk(data, response) as Promise<ChatReactionsResponse>;
  }

  if (mode === 'artist') {
    const { data, response } = await api.GET('/artist/messages/reactions/{messageId}', {
      params: { path: { messageId } },
      signal: options.signal,
    });
    return ensurePayloadOk(data, response) as Promise<ChatReactionsResponse>;
  }

  const artistId = requireParam(params.artistId, 'artistId');
  const { data, response } = await api.GET('/admin/messages/{artistId}/reactions/{messageId}', {
    params: { path: { artistId, messageId } },
    signal: options.signal,
  });
  return ensurePayloadOk(data, response) as Promise<ChatReactionsResponse>;
}

export async function toggleChatMessageReaction(
  params: ChatReactionParams
): Promise<Record<string, unknown>> {
  const mode = params.mode || 'admin';
  const scope = params.scope || 'individual';
  const messageId = requireParam(params.messageId, 'messageId');
  const body = normalizeReactionBody(params.reactionType);

  if (scope === 'group') {
    const threadId = requireParam(params.threadId, 'threadId');
    const result =
      mode === 'artist'
        ? await api.POST('/artist/group-chat/threads/{threadId}/reactions/{messageId}', {
            params: { path: { threadId, messageId } },
            body,
          })
        : await api.POST('/admin/group-chat/threads/{threadId}/reactions/{messageId}', {
            params: { path: { threadId, messageId } },
            body,
          });
    return ensurePayloadOk(result.data, result.response);
  }

  if (scope === 'event') {
    const eventId = requireParam(params.eventId, 'eventId');
    const { data, response } = await api.POST('/event-chat/events/{eventId}/reactions/{messageId}', {
      params: { path: { eventId, messageId } },
      body,
    });
    return ensurePayloadOk(data, response);
  }

  if (mode === 'artist') {
    const { data, response } = await api.POST('/artist/messages/reactions/{messageId}', {
      params: { path: { messageId } },
      body,
    });
    return ensurePayloadOk(data, response);
  }

  const artistId = requireParam(params.artistId, 'artistId');
  const { data, response } = await api.POST('/admin/messages/{artistId}/reactions/{messageId}', {
    params: { path: { artistId, messageId } },
    body,
  });
  return ensurePayloadOk(data, response);
}
